import type { TFunction } from 'i18next';
import { apiClient } from '@/lib/api-client';
import type { AppLocale } from '@/i18n/config';
import { formatAmountInput, parseAmountMinor, parseCompetenceMonth } from '../finance-format';
import type { FinanceMovement } from './dashboard-model';

type FinanceT = TFunction<'finance'>;

export type ForecastDirection = 'inflow' | 'outflow';
export type ForecastFormMode = 'create' | 'edit' | null;
export type ForecastFormValues = {
  competenceMonth: string;
  amount: string;
  direction: ForecastDirection;
  category: string;
  description: string;
  economicRelevant: boolean;
};
export type ForecastFormErrors = Partial<Record<keyof ForecastFormValues, string>>;

export function createEmptyForecastForm(): ForecastFormValues {
  return {
    competenceMonth: '',
    amount: '',
    direction: 'outflow',
    category: '',
    description: '',
    economicRelevant: false,
  };
}

function readProblemDetail(value: unknown): string | null {
  if (typeof value !== 'object' || value === null || !('detail' in value)) {
    return null;
  }

  return typeof value.detail === 'string' ? value.detail : null;
}

function readMutationProblemDetail(result: {
  response: { ok: boolean };
  error?: unknown;
}): string | null {
  return result.response.ok ? null : readProblemDetail(result.error);
}

export type ForecastValidation = {
  errors: ForecastFormErrors;
  /** Set only when there are no errors at all. */
  parsed: { competenceMonth: string; amountMinor: number } | null;
};

export function validateForecastForm(
  values: ForecastFormValues,
  locale: AppLocale,
  t: FinanceT,
): ForecastValidation {
  const competenceMonth = parseCompetenceMonth(values.competenceMonth);
  const amountMinor = parseAmountMinor(values.amount, locale);

  const errors: ForecastFormErrors = {};
  if (competenceMonth === null) {
    errors.competenceMonth = t('dashboard.forecast.validation.competenceMonth');
  }
  if (amountMinor === null) {
    errors.amount = t('dashboard.forecast.validation.amount');
  }
  if (values.category.trim().length === 0) {
    errors.category = t('dashboard.forecast.validation.category');
  }
  if (values.description.trim().length === 0) {
    errors.description = t('dashboard.forecast.validation.description');
  }

  if (Object.keys(errors).length > 0 || amountMinor === null || competenceMonth === null) {
    return { errors, parsed: null };
  }

  return { errors, parsed: { competenceMonth, amountMinor } };
}

export type ForecastSubmitInput = {
  editingMovementId: string | null;
  mode: ForecastFormMode;
  values: ForecastFormValues;
  parsed: { competenceMonth: string; amountMinor: number };
  reload: () => Promise<void>;
  t: FinanceT;
};

/** The write error to show, or `null` when the write landed and the data was reloaded. */
export async function submitValidForecast(input: ForecastSubmitInput): Promise<string | null> {
  const { editingMovementId, mode, values, parsed, reload, t } = input;

  // `openapi-fetch` RETHROWS transport-level failures — no `onError` middleware is registered in
  // `api-client.ts` — and `usePendingWrite.run` is try/finally with no catch, while the call site
  // invokes this with `void`. Without this try/catch a flaky connection or a Cloud Run cold-start
  // reset left the rejection unhandled, `forecastApiError` null and the form untouched: the CEO
  // got no indication the Forecast had not been saved, retried, and risked a duplicate movement
  // (SL-0053 review, console finding 2). The read path above already had its own try/catch.
  try {
    const failure = await writeForecast({ editingMovementId, mode, values, ...parsed });
    if (failure !== null) {
      return failure.detail ?? t('dashboard.forecast.writeError');
    }

    await reload();
  } catch {
    return t('dashboard.forecast.writeError');
  }

  return null;
}

export function forecastValuesFromMovement(
  movement: FinanceMovement,
  locale: AppLocale,
): ForecastFormValues {
  return {
    competenceMonth: movement.competenceMonth.slice(0, 7),
    amount: formatAmountInput(movement.amountMinor, locale),
    direction: movement.direction,
    category: movement.category,
    description: movement.description,
    economicRelevant: movement.economicRelevant,
  };
}

export type ForecastWriteInput = {
  editingMovementId: string | null;
  mode: ForecastFormMode;
  values: ForecastFormValues;
  competenceMonth: string;
  amountMinor: number;
};

/** `null` when the write landed, otherwise the server's problem detail (itself possibly `null`). */
export type ForecastWriteFailure = { detail: string | null };

export async function writeForecast(
  input: ForecastWriteInput,
): Promise<ForecastWriteFailure | null> {
  const { editingMovementId, mode, values, competenceMonth, amountMinor } = input;

  if (mode === 'edit' && editingMovementId !== null) {
    const result = await apiClient.PATCH('/v1/finance/forecasts/{movementId}', {
      params: { path: { movementId: editingMovementId } },
      body: {
        amountMinor,
        description: values.description.trim(),
      },
    });

    return result.response.ok ? null : { detail: readMutationProblemDetail(result) };
  }

  const result = await apiClient.POST('/v1/finance/forecasts', {
    body: {
      competenceMonth,
      amountMinor,
      direction: values.direction,
      category: values.category.trim(),
      description: values.description.trim(),
      economicRelevant: values.economicRelevant,
    },
  });

  return result.response.ok ? null : { detail: readMutationProblemDetail(result) };
}
